import { CompactNumber } from "@/shared/ui/CompactNumber";
import { Card, CardContent } from "@/components/ui/card";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  DollarSignIcon,
  ShoppingCartIcon,
  TrendingDownIcon,
} from "lucide-react";

type WalletReportsSummaryCardsProps = {
  totalTopup: number;
  totalUsage: number;
  totalRefund: number;
  totalTransactions: number;
  topCustomerName: string | null;
  isLoading?: boolean;
};

export function WalletReportsSummaryCards({
  totalTopup,
  totalUsage,
  totalRefund,
  totalTransactions,
  topCustomerName,
  isLoading,
}: WalletReportsSummaryCardsProps) {
  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {[1, 2, 3, 4, 5].map((i) => (
          <Card key={i} className="rounded-xl border bg-card">
            <CardContent className="">
              <div className="h-10 animate-pulse rounded bg-muted" />
              <div className="mt-2 h-6 w-16 animate-pulse rounded bg-muted" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
      {/* TOTAL TOPUP */}
      <Card className="rounded-xl border border-green-500/20 bg-card">
        <CardContent className="flex items-center gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-green-500/10">
            <ArrowUpIcon className="size-5 text-green-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-muted-foreground text-sm">ຍອດເຕີມເງິນ</p>
            <p className="font-semibold text-xl tabular-nums">
              <CompactNumber value={totalTopup} suffix=" LAK" />
            </p>
          </div>
        </CardContent>
      </Card>

      {/* TOTAL USAGE */}
      <Card className="rounded-xl border border-red-500/20 bg-card">
        <CardContent className="flex items-center gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-red-500/10">
            <ArrowDownIcon className="size-5 text-red-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-muted-foreground text-sm">ຍອດໃຊ້ຈ່າຍ</p>
            <p className="font-semibold text-xl tabular-nums">
              <CompactNumber value={totalUsage} suffix=" LAK" />
            </p>
          </div>
        </CardContent>
      </Card>

      {/* TOTAL REFUND */}
      <Card className="rounded-xl border border-blue-500/20 bg-card">
        <CardContent className="flex items-center gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-blue-500/10">
            <TrendingDownIcon className="size-5 text-blue-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-muted-foreground text-sm">ຍອດຄືນເງິນ</p>
            <p className="font-semibold text-xl tabular-nums">
              <CompactNumber value={totalRefund} suffix=" LAK" />
            </p>
          </div>
        </CardContent>
      </Card>

      {/* TOTAL TRANSACTIONS */}
      <Card className="rounded-xl border border-purple-500/20 bg-card">
        <CardContent className="flex items-center gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-purple-500/10">
            <ShoppingCartIcon className="size-5 text-purple-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-muted-foreground text-sm">ຈຳນວນທຸລະກຳ</p>
            <p className="font-semibold text-xl tabular-nums">
              <CompactNumber value={totalTransactions} />
            </p>
          </div>
        </CardContent>
      </Card>

      {/* TOP CUSTOMER */}
      <Card className="rounded-xl border border-orange-500/20 bg-card">
        <CardContent className="flex items-center gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-orange-500/10">
            <DollarSignIcon className="size-5 text-orange-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-muted-foreground text-sm">ລູກຄ້າອັນດັບ 1</p>
            <p className="truncate font-semibold text-lg tabular-nums">
              {topCustomerName ?? "–"}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
